import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveFromApi, today } from './fileSave';

export const SHORTCUTS = [
  ['Ctrl+N', 'New drink'],
  ['Ctrl+S', 'Save (in the editor)'],
  ['Ctrl+F', 'Search'],
  ['Ctrl+E', 'Export all drinks to Excel'],
] as const;

/** Route state that tells the Library to focus its search box on arrival. */
export const FOCUS_SEARCH = { focusSearch: true } as const;

/**
 * App-wide shortcuts (Cmd on a Mac). Ctrl+S is handled by the editor itself.
 * Note: browsers reserve Ctrl+N for "new window", so that one works in the desktop app and
 * installed PWA, but not in a normal browser tab.
 */
export function useShortcuts(onMessage: (text: string, opts?: { error?: boolean }) => void): void {
  const navigate = useNavigate();
  const latest = useRef({ navigate, onMessage });
  latest.current = { navigate, onMessage };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const { navigate, onMessage } = latest.current;
      switch (e.key.toLowerCase()) {
        case 'n':
          e.preventDefault();
          navigate('/drinks/new');
          break;
        case 'f': {
          e.preventDefault();
          const search = document.querySelector<HTMLInputElement>('input[type="search"]');
          if (search) {
            search.focus();
            search.select();
          } else {
            navigate('/', { state: FOCUS_SEARCH });
          }
          break;
        }
        case 'e':
          e.preventDefault();
          saveFromApi('/export/xlsx', `muddle-cocktails-${today()}.xlsx`).then(
            (r) => r.saved && onMessage('Export saved'),
            (err: unknown) =>
              onMessage(err instanceof Error ? err.message : 'Export failed.', { error: true }),
          );
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

/** Sets the browser/window title for the current page. */
export function useTitle(title: string): void {
  useEffect(() => {
    document.title = title ? `${title} · Muddle` : 'Muddle';
  }, [title]);
}
